'use client';

import React from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface ServiceFeature {
  icon: string;
  text: string;
}

interface ServiceCardProps {
  title: string;
  description: string;
  image: string;
  alt: string;
  price: string;
  features: ServiceFeature[];
  popular?: boolean;
  onQuoteClick: () => void;
}

const ServiceCard = ({
  title,
  description,
  image,
  alt,
  price,
  features,
  popular = false,
  onQuoteClick,
}: ServiceCardProps) => {
  return (
    <div
      className={`relative bg-card rounded-lg overflow-hidden shadow-soft hover:shadow-elevated transition-all duration-base card-hover ${
        popular ? 'ring-2 ring-brand-purple' : ''
      }`}
    >
      {popular && (
        <div className="absolute top-4 right-4 z-10 bg-brand-purple text-white px-3 py-1 rounded-full text-sm font-semibold">
          Most Popular
        </div>
      )}

      <div className="relative h-48 overflow-hidden">
        <AppImage
          src={image}
          alt={alt}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary/80 to-transparent" />
      </div>

      <div className="p-6">
        <h3 className="text-2xl font-headline font-bold text-primary mb-2">
          {title}
        </h3>
        <p className="text-text-secondary mb-4 line-clamp-2">{description}</p>

        <div className="flex items-baseline mb-4">
          <span className="text-3xl font-headline font-bold text-brand-purple">
            {price}
          </span>
          <span className="text-text-secondary ml-2">KWD</span>
        </div>

        <div className="space-y-3 mb-6">
          {features.map((feature) => (
            <div key={`${feature.icon}-${feature.text}`} className="flex items-start space-x-3">
              <Icon
                name={feature.icon as any}
                size={20}
                variant="solid"
                className="text-success mt-0.5 flex-shrink-0"
              />
              <span className="text-sm text-text-primary">{feature.text}</span>
            </div>
          ))}
        </div>

        <button
          onClick={onQuoteClick}
          className="w-full flex items-center justify-center space-x-2 px-6 py-3 rounded-lg bg-secondary text-secondary-foreground hover:bg-opacity-90 transition-all duration-base font-body font-semibold"
        >
          <Icon name="CalculatorIcon" size={20} variant="solid" />
          <span>Get Quote</span>
        </button>
      </div>
    </div>
  );
};

export default ServiceCard;